import type { HttpAgent } from "@icp-sdk/core/agent";
import type { Principal } from "@icp-sdk/core/principal";
import {
	type CollateralKey,
	toCandidCollateral,
} from "./candid-helpers";
import type { IcClientConfig } from "./config";
import {
	approveIcrcSpender,
	approveUsdmForProtocol,
	resolveTransferLedgerId,
} from "./icrc";
import { unwrapResult } from "./result";

type ChainLedgers = {
	usdm: [] | [Principal];
	mmk: [] | [Principal];
};

/** Desk fee + net quote (pure; mirrors SurplusLib.quoteSwapUsdmForAsset). */
export function quoteUsdmForAssetLocal(
	usdmIn: number,
	assetPrice: number,
	feeBps: number,
): { fee: number; usdmNet: number; assetOut: number } {
	if (!(usdmIn > 0) || !(assetPrice > 0)) {
		return { fee: 0, usdmNet: 0, assetOut: 0 };
	}
	const fee = (usdmIn * feeBps) / 10_000;
	const usdmNet = Math.max(0, usdmIn - fee);
	const assetOut = usdmNet / assetPrice;
	return { fee, usdmNet, assetOut };
}

/** Desk fee + net quote (pure; mirrors SurplusLib.quoteSwapAssetForUsdm). */
export function quoteAssetForUsdmLocal(
	assetIn: number,
	assetPrice: number,
	feeBps: number,
): { fee: number; usdmGross: number; usdmOut: number } {
	if (!(assetIn > 0) || !(assetPrice > 0)) {
		return { fee: 0, usdmGross: 0, usdmOut: 0 };
	}
	const usdmGross = assetIn * assetPrice;
	const fee = (usdmGross * feeBps) / 10_000;
	const usdmOut = Math.max(0, usdmGross - fee);
	return { fee, usdmGross, usdmOut };
}

export type DeskAsset = CollateralKey;

type DeskActor = {
	swapUsdmForAsset: (
		asset: ReturnType<typeof toCandidCollateral>,
		usdmInE8: bigint,
	) => Promise<unknown>;
	swapAssetForUsdm: (
		asset: ReturnType<typeof toCandidCollateral>,
		assetInE8: bigint,
	) => Promise<unknown>;
};

/** Approve USDM and buy sleeve asset on the liquidity desk. */
export async function swapUsdmForAsset(
	agent: HttpAgent,
	actor: DeskActor,
	config: IcClientConfig,
	ledgers: ChainLedgers,
	asset: DeskAsset,
	usdmAmount: number,
	amountToNat: (n: number) => bigint,
): Promise<bigint> {
	await approveUsdmForProtocol(agent, config, ledgers, usdmAmount);
	const raw = await actor.swapUsdmForAsset(
		toCandidCollateral(asset),
		amountToNat(usdmAmount),
	);
	return unwrapResult(raw, "swapUsdmForAsset") as bigint;
}

/** Approve coll and sell into the liquidity desk for USDM. */
export async function swapAssetForUsdm(
	agent: HttpAgent,
	actor: DeskActor,
	config: IcClientConfig,
	ledgers: ChainLedgers,
	asset: DeskAsset,
	assetAmount: number,
	amountToNat: (n: number) => bigint,
): Promise<bigint> {
	const ledgerId = resolveTransferLedgerId(config, asset, ledgers);
	if (!ledgerId) {
		throw new Error(`${asset} ledger not configured`);
	}
	await approveIcrcSpender(
		agent,
		ledgerId,
		config.protocolCanisterId,
		assetAmount,
	);
	const raw = await actor.swapAssetForUsdm(
		toCandidCollateral(asset),
		amountToNat(assetAmount),
	);
	return unwrapResult(raw, "swapAssetForUsdm") as bigint;
}
